<template>
        <div class="p-6">
                <div class="flex justify-between items-start mb-6">
                        <div>
                                <h1 class="text-2xl font-semibold text-gray-800 m-0">Discounts</h1>
                                <p class="text-gray-500 mt-1 mb-0">Manage promotional discounts and voucher codes</p>
                        </div>
                        <button
                                class="inline-flex items-center gap-2 py-2.5 px-4 bg-orange-7 text-white border-none rounded-lg text-sm font-medium cursor-pointer transition-colors hover:bg-orange-6"
                                @click="openCreateModal"
                        >
                                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                </svg>
                                Create New
                        </button>
                </div>

                <div class="bg-white rounded-lg shadow-sm overflow-hidden">
                        <div class="p-4 px-6 border-b border-gray-200">
                                <div class="flex flex-wrap items-center justify-between gap-4">
                                        <div class="flex items-center gap-2">
                                                <span class="font-semibold text-gray-700">Discounts</span>
                                                <span class="bg-orange-2 text-orange-7 py-0.5 px-2 rounded-full text-xs font-medium">
                                                        {{ filteredDiscounts.length }} total
                                                </span>
                                        </div>
                                        <div class="flex items-center gap-3">
                                                <div class="relative">
                                                        <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                                        </svg>
                                                        <input
                                                                v-model="searchQuery"
                                                                type="text"
                                                                placeholder="Search by name..."
                                                                class="py-2 px-3 pl-9 border border-gray-300 rounded-md text-sm w-52 outline-none focus:border-orange-7 focus:ring-2 focus:ring-orange-2"
                                                        >
                                                </div>
                                                <button
                                                        class="inline-flex items-center gap-1.5 py-2 px-3 bg-white border border-gray-300 rounded-md text-sm text-gray-700 cursor-pointer hover:bg-gray-50"
                                                        @click="refreshData"
                                                >
                                                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                        </svg>
                                                        Refresh
                                                </button>
                                        </div>
                                </div>
                        </div>

                        <div v-if="loading" class="flex justify-center items-center p-12">
                                <div class="w-8 h-8 border-2 border-gray-200 border-t-orange-7 rounded-full animate-spin"></div>
                        </div>

                        <div v-else-if="filteredDiscounts.length === 0" class="text-center p-12 text-gray-500">
                                <svg class="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                                </svg>
                                <p class="m-0">No discounts found</p>
                        </div>

                        <table v-else class="w-full border-collapse">
                                <thead>
                                        <tr class="bg-gray-50 border-b border-gray-200">
                                                <th class="py-3 px-6 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Description</th>
                                                <th class="py-3 px-6 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Tracker</th>
                                                <th class="py-3 px-6 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Vouchers</th>
                                                <th class="py-3 px-6 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Issued</th>
                                                <th class="py-3 px-6 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Valid</th>
                                                <th class="py-3 px-6 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Used</th>
                                                <th class="py-3 px-6 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Value</th>
                                                <th class="py-3 px-6 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                                        </tr>
                                </thead>
                                <tbody>
                                        <tr
                                                v-for="discount in paginatedDiscounts"
                                                :key="discount.id"
                                                class="border-b border-gray-200 transition-colors hover:bg-gray-50"
                                        >
                                                <td class="p-4 px-6 text-sm font-medium text-gray-900">
                                                        {{ discount.name }}
                                                </td>
                                                <td class="p-4 px-6 text-sm text-gray-500">
                                                        {{ getTrackerName(discount.trackerid) }}
                                                </td>
                                                <td class="p-4 px-6 text-center text-sm text-gray-700">
                                                        {{ discount.voucherCount || 0 }}
                                                </td>
                                                <td class="p-4 px-6 text-center text-sm text-gray-700">
                                                        {{ discount.voucherIssued || 0 }}
                                                </td>
                                                <td class="p-4 px-6 text-center text-sm text-gray-700">
                                                        {{ discount.voucherValidity || 0 }}
                                                </td>
                                                <td class="p-4 px-6 text-center text-sm text-gray-700">
                                                        {{ discount.voucherUsed || 0 }}
                                                </td>
                                                <td class="p-4 px-6 text-right text-sm font-medium text-green-600">
                                                        {{ formatValue(discount) }}
                                                </td>
                                                <td class="p-4 px-6 text-right">
                                                        <div class="flex items-center justify-end gap-1">
                                                                <button
                                                                        class="p-1.5 bg-transparent border-none text-orange-7 cursor-pointer rounded transition-colors hover:bg-orange-2"
                                                                        title="Edit discount"
                                                                        @click="editDiscount(discount)"
                                                                >
                                                                        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                                        </svg>
                                                                </button>
                                                                <button
                                                                        class="p-1.5 bg-transparent border-none text-red-500 cursor-pointer rounded transition-colors hover:bg-red-50"
                                                                        title="Delete discount"
                                                                        @click="confirmDeleteDiscount(discount)"
                                                                >
                                                                        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                                        </svg>
                                                                </button>
                                                        </div>
                                                </td>
                                        </tr>
                                </tbody>
                        </table>

                        <div v-if="filteredDiscounts.length > 0" class="flex flex-wrap items-center justify-between py-3 px-6 border-t border-gray-200 gap-4">
                                <div class="flex items-center gap-2">
                                        <span class="text-sm text-gray-500">Rows per page:</span>
                                        <select
                                                v-model="pageSize"
                                                class="py-1 px-2 border border-gray-300 rounded text-sm outline-none"
                                        >
                                                <option :value="10">10</option>
                                                <option :value="25">25</option>
                                                <option :value="50">50</option>
                                        </select>
                                </div>
                                <div class="flex items-center gap-2">
                                        <span class="text-sm text-gray-500">
                                                {{ (currentPage - 1) * pageSize + 1 }}-{{ Math.min(currentPage * pageSize, filteredDiscounts.length) }} of {{ filteredDiscounts.length }}
                                        </span>
                                        <button
                                                :disabled="currentPage === 1"
                                                class="p-1.5 border border-gray-300 rounded bg-white cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                                @click="currentPage--"
                                        >
                                                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                                                </svg>
                                        </button>
                                        <button
                                                :disabled="currentPage >= totalPages"
                                                class="p-1.5 border border-gray-300 rounded bg-white cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                                @click="currentPage++"
                                        >
                                                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                                                </svg>
                                        </button>
                                </div>
                        </div>
                </div>

                <div
                        v-if="showModal"
                        class="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
                        @click.self="closeModal"
                >
                        <div class="bg-white rounded-lg p-6 w-full max-w-lg shadow-2xl">
                                <h3 class="text-lg font-semibold text-gray-900 m-0 mb-6">Create New Discount</h3>
                                <div class="flex flex-col gap-4">
                                        <div>
                                                <label class="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                                                <input
                                                        v-model="newDiscount.name"
                                                        type="text"
                                                        placeholder="Enter discount name"
                                                        class="w-full py-2 px-3 border border-gray-300 rounded-md text-sm outline-none box-border focus:border-orange-7"
                                                >
                                        </div>
                                        <div>
                                                <label class="block text-sm font-medium text-gray-700 mb-1">Tracker</label>
                                                <select
                                                        v-model="newDiscount.trackerid"
                                                        class="w-full py-2 px-3 border border-gray-300 rounded-md text-sm outline-none bg-white box-border"
                                                >
                                                        <option :value="null">-- Select Tracker --</option>
                                                        <option v-for="tracker in trackers" :key="tracker.id" :value="tracker.id">{{ tracker.name }}</option>
                                                </select>
                                        </div>
                                        <div class="grid grid-cols-2 gap-4">
                                                <div>
                                                        <label class="block text-sm font-medium text-gray-700 mb-1">Relative %</label>
                                                        <input
                                                                v-model.number="newDiscount.relative"
                                                                type="number"
                                                                placeholder="0"
                                                                class="w-full py-2 px-3 border border-gray-300 rounded-md text-sm outline-none box-border"
                                                        >
                                                </div>
                                                <div>
                                                        <label class="block text-sm font-medium text-gray-700 mb-1">Absolute (cents)</label>
                                                        <input
                                                                v-model.number="newDiscount.absolute"
                                                                type="number"
                                                                placeholder="0"
                                                                class="w-full py-2 px-3 border border-gray-300 rounded-md text-sm outline-none box-border"
                                                        >
                                                </div>
                                        </div>
                                </div>
                                <div class="flex justify-end gap-3 mt-6">
                                        <button
                                                class="py-2 px-4 bg-white border border-gray-300 rounded-md text-sm text-gray-700 cursor-pointer hover:bg-gray-50"
                                                @click="closeModal"
                                        >
                                                Cancel
                                        </button>
                                        <button
                                                :disabled="!newDiscount.name || saving"
                                                class="py-2 px-4 bg-orange-7 text-white border-none rounded-md text-sm font-medium cursor-pointer disabled:opacity-50 hover:bg-orange-6"
                                                @click="createDiscount"
                                        >
                                                {{ saving ? 'Creating...' : 'Create' }}
                                        </button>
                                </div>
                        </div>
                </div>
        </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { DiscountModel, AffiliateModel, CurrencyModel } from '@sosocio/creator/interfaces';
import DiscountService from 'services/DiscountService';
import AffiliateService from 'services/AffiliateService';
import CurrencyService from 'services/CurrencyService';
import Swal from 'sweetalert2';

interface ExtendedDiscountModel extends DiscountModel {
        voucherCount?: number;
        voucherIssued?: number;
        voucherValidity?: number;
        voucherUsed?: number;
}

const showToast = (message: string, icon: 'success' | 'error' = 'success') => {
        Swal.fire({
                toast: true,
                position: 'top-end',
                icon,
                title: message,
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true,
                customClass: {
                        popup: 'windmill-swal-toast',
                },
        });
};

export default defineComponent({
        name: 'DiscountsPage',
        setup() {
                const router = useRouter();
                
                const discounts = ref<ExtendedDiscountModel[]>([]);
                const trackers = ref<AffiliateModel[]>([]);
                const currencies = ref<Record<string, string>>({});
                const loading = ref(false);
                const saving = ref(false);
                const showModal = ref(false);
                const searchQuery = ref('');
                const currentPage = ref(1);
                const pageSize = ref(25);
                
                const newDiscount = ref({
                        name: '',
                        trackerid: null as number | null,
                        relative: 0,
                        absolute: 0,
                });
                
                const filteredDiscounts = computed(() => {
                        if (!searchQuery.value) return discounts.value;
                        const query = searchQuery.value.toLowerCase();
                        return discounts.value.filter(d =>
                                d.name?.toLowerCase().includes(query)
                        );
                });
                
                const totalPages = computed(() => Math.ceil(filteredDiscounts.value.length / pageSize.value));
                
                const paginatedDiscounts = computed(() => {
                        const start = (currentPage.value - 1) * pageSize.value;
                        return filteredDiscounts.value.slice(start, start + pageSize.value);
                });
                
                const getTrackerName = (trackerId: number | null) => {
                        if (!trackerId) return '-';
                        const tracker = trackers.value.find(t => t.id === trackerId);
                        return tracker?.name || '-';
                };
                
                const formatValue = (discount: ExtendedDiscountModel) => {
                        if (discount.relative) {
                                return `${discount.relative}%`;
                        }
                        if (discount.absolute) {
                                return `${(discount.absolute / 100).toFixed(2)}`;
                        }
                        return '-';
                };
                
                const fetchData = async () => {
                        loading.value = true;
                        try {
                                const [discountsData, trackersData, currenciesData] = await Promise.all([
                                        DiscountService.getAll(new URLSearchParams({ limit: '0' })),
                                        AffiliateService.getAll(new URLSearchParams({ limit: '0' })),
                                        CurrencyService.getAll(new URLSearchParams({ limit: '0' })),
                                ]);
                                
                                discounts.value = discountsData;
                                trackers.value = trackersData;
                                
                                const currencyMap: Record<string, string> = {};
                                currenciesData.forEach((c: CurrencyModel) => {
                                        currencyMap[c.id] = c.sign || c.name || '';
                                });
                                currencies.value = currencyMap;
                        } catch (error: any) {
                                showToast(error.message || 'Failed to load data', 'error');
                        } finally {
                                loading.value = false;
                        }
                };
                
                const refreshData = () => {
                        fetchData();
                };
                
                const openCreateModal = () => {
                        newDiscount.value = {
                                name: '',
                                trackerid: null,
                                relative: 0,
                                absolute: 0,
                        };
                        showModal.value = true;
                };
                
                const closeModal = () => {
                        showModal.value = false;
                };
                
                const createDiscount = async () => {
                        if (!newDiscount.value.name) return;
                        
                        saving.value = true;
                        try {
                                const created = await DiscountService.create({
                                        name: newDiscount.value.name,
                                        trackerid: newDiscount.value.trackerid ?? undefined,
                                        relative: newDiscount.value.relative || 0,
                                        absolute: newDiscount.value.absolute || 0,
                                });
                                
                                closeModal();
                                showToast('Discount created successfully');
                                router.push(`/discount/${created.id}`);
                        } catch (error: any) {
                                showToast(error.message || 'Failed to create discount', 'error');
                        } finally {
                                saving.value = false;
                        }
                };
                
                const editDiscount = (discount: ExtendedDiscountModel) => {
                        router.push(`/discount/${discount.id}`);
                };
                
                const confirmDeleteDiscount = async (discount: ExtendedDiscountModel) => {
                        const result = await Swal.fire({
                                title: 'Delete Discount?',
                                text: `Are you sure you want to delete "${discount.name}"? This action cannot be undone.`,
                                icon: 'warning',
                                showCancelButton: true,
                                confirmButtonColor: '#dc2626',
                                cancelButtonColor: '#6b7280',
                                confirmButtonText: 'Yes, delete it',
                        });
                        
                        if (result.isConfirmed) {
                                try {
                                        await DiscountService.delete(discount.id);
                                        discounts.value = discounts.value.filter(d => d.id !== discount.id);
                                        showToast('Discount deleted successfully');
                                } catch (error: any) {
                                        showToast(error.message || 'Failed to delete discount', 'error');
                                }
                        }
                };
                
                onMounted(() => {
                        fetchData();
                });
                
                return {
                        discounts,
                        trackers,
                        loading,
                        saving,
                        showModal,
                        searchQuery,
                        currentPage,
                        pageSize,
                        newDiscount,
                        filteredDiscounts,
                        totalPages,
                        paginatedDiscounts,
                        getTrackerName,
                        formatValue,
                        refreshData,
                        openCreateModal,
                        closeModal,
                        createDiscount,
                        editDiscount,
                        confirmDeleteDiscount,
                };
        },
});
</script>
